import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { tokens as c } from '../../theme';
import { readTablePref, writeTablePref } from './tableUtils';

/**
 * Show / hide a page's filters (owner, 29 Oct: pages with many filters, such
 * as Drivers, need it). Each person's choice is kept in this browser for that
 * page; filters stay applied while hidden, so the page must keep showing its
 * active-filter chips outside the hidden part.
 *
 *   const [filtersOpen, toggleFilters] = useFiltersOpen('drivers');
 *   <FilterToggleButton open={filtersOpen} onClick={toggleFilters} activeCount={activeTokens.length} />
 *   <Collapse in={filtersOpen}>…the filter chips…</Collapse>
 */
export function useFiltersOpen(prefsKey, initialOpen = true) {
  const uid = useSelector((state) => (state.auth && state.auth.profile ? state.auth.profile.uid : null));
  const [open, setOpen] = useState(() => readTablePref(prefsKey, 'filters_open', uid, initialOpen) !== false);
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeTablePref(prefsKey, 'filters_open', uid, next);
      return next;
    });
  }, [prefsKey, uid]);
  return [open, toggle];
}

/** "Hide filters" / "Show filters · 2 on": a 44-px outlined button. */
export function FilterToggleButton({ open, onClick, activeCount = 0, controls, sx }) {
  const { t } = useTranslation();
  const label = open ? t('filters_hide', { defaultValue: 'Hide filters' }) : t('filters_show', { defaultValue: 'Show filters' });
  return (
    <Button
      variant="outlined"
      color="neutral"
      onClick={onClick}
      aria-expanded={open ? 'true' : 'false'}
      aria-controls={controls}
      startIcon={<TuneRoundedIcon />}
      endIcon={open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
      sx={{ minHeight: 44, whiteSpace: 'nowrap', ...(sx || {}) }}
    >
      {label}
      {!open && activeCount > 0 ? (
        <Box
          component="span"
          sx={{ ml: 1, px: 0.75, minWidth: 22, borderRadius: '11px', bgcolor: c.ACCENT_TINT, color: c.ACCENT_TEXT, fontSize: 13, fontWeight: 700, lineHeight: '22px', textAlign: 'center' }}
        >
          {t('filters_on_count', { count: activeCount, defaultValue: '{{count}} on' })}
        </Box>
      ) : null}
    </Button>
  );
}
